"use client";

import { useRef, type ReactNode } from "react";
import { gsap, useGSAP } from "@/lib/gsap-setup";

/** Entrada suave al entrar el bloque en el viewport (scroll). */
export function RevealOnScroll({
  children,
  y = 28,
  duration = 0.7,
}: {
  children: ReactNode;
  y?: number;
  duration?: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      const el = root.current;
      if (!el) return;
      const tween = gsap.fromTo(
        el,
        { opacity: 0, y },
        {
          opacity: 1,
          y: 0,
          duration,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        },
      );
      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
      };
    },
    { scope: root },
  );
  return <div ref={root}>{children}</div>;
}

export function FadeIn({
  children,
  delay = 0,
  y = 16,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      const el = root.current;
      if (!el) return;
      gsap.fromTo(el, { opacity: 0, y }, { opacity: 1, y: 0, duration: 0.45, ease: "power2.out", delay });
    },
    { scope: root },
  );
  return <div ref={root}>{children}</div>;
}

export function Stagger({ children }: { children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  useGSAP(
    () => {
      const items = root.current?.querySelectorAll("[data-stagger-item]");
      if (!items?.length) return;
      gsap.fromTo(
        items,
        { opacity: 0, y: 12 },
        {
          opacity: 1,
          y: 0,
          duration: 0.35,
          ease: "power2.out",
          stagger: 0.08,
          delay: 0.04,
        },
      );
    },
    { scope: root },
  );
  return <div ref={root}>{children}</div>;
}

export function StaggerItem({ children }: { children: ReactNode }) {
  return <div data-stagger-item>{children}</div>;
}
